import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, Building2, Mail, Lock, User, Phone, MapPin,
  CheckCircle, Loader2, Eye, EyeOff, Gift, FileText, Palette, Upload, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { companyApi } from '../lib/api';

// Logo component - Reduced size for onboarding
const Logo = ({ className = "h-8" }) => {
  return (
    <img 
      src="/logo.png" 
      alt="ResaWork" 
      className={className}
    />
  );
};

const STEPS = [
  { id: 'company', title: 'Votre entreprise', icon: Building2 },
  { id: 'billing', title: 'Facturation', icon: FileText },
  { id: 'branding', title: 'Personnalisation', icon: Palette },
  { id: 'contact', title: 'Contact', icon: User },
  { id: 'account', title: 'Compte', icon: Lock },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Company info
    name: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    // Billing info
    billing_address: '',
    billing_city: '',
    billing_postal_code: '',
    billing_country: 'France',
    use_same_address: true,
    // Branding
    logo_url: '',
    primary_color: '#84CC16',
    // Contact person
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_role: '',
    // Account
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});

  const validateStep = () => {
    const newErrors = {};
    
    switch (currentStep) {
      case 0: // Company
        if (!formData.name.trim()) newErrors.name = 'Le nom de l\'entreprise est requis';
        if (!formData.address.trim()) newErrors.address = 'L\'adresse est requise';
        if (!formData.city.trim()) newErrors.city = 'La ville est requise';
        if (!formData.postal_code.trim()) newErrors.postal_code = 'Le code postal est requis';
        break;
      case 1: // Billing
        if (!formData.use_same_address) {
          if (!formData.billing_address.trim()) newErrors.billing_address = 'L\'adresse de facturation est requise';
          if (!formData.billing_city.trim()) newErrors.billing_city = 'La ville est requise';
          if (!formData.billing_postal_code.trim()) newErrors.billing_postal_code = 'Le code postal est requis';
        }
        break;
      case 2: // Branding - No required fields
        break;
      case 3: // Contact
        if (!formData.contact_name.trim()) newErrors.contact_name = 'Le nom du contact est requis';
        if (!formData.contact_email.trim()) newErrors.contact_email = 'L\'email du contact est requis';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
          newErrors.contact_email = 'Email invalide';
        }
        break;
      case 4: // Account
        if (!formData.email.trim()) newErrors.email = 'L\'email est requis';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Email invalide';
        }
        if (!formData.password) newErrors.password = 'Le mot de passe est requis';
        else if (formData.password.length < 6) {
          newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Le fichier est trop volumineux (max 2 Mo)');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        updateForm('logo_url', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setIsLoading(true);
    try {
      // Build full address for billing
      const billingAddress = formData.use_same_address 
        ? `${formData.address}, ${formData.postal_code} ${formData.city}, ${formData.country}`
        : `${formData.billing_address}, ${formData.billing_postal_code} ${formData.billing_city}, ${formData.billing_country}`;

      await companyApi.register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.contact_phone,
        address: `${formData.address}, ${formData.postal_code} ${formData.city}, ${formData.country}`,
        siret: formData.siret,
        billing_address: billingAddress,
        logo_url: formData.logo_url,
        primary_color: formData.primary_color,
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        contact_role: formData.contact_role,
        default_hourly_rate: 35, // Default, can be changed later in settings
      });
      
      setIsComplete(true);
      toast.success('Inscription réussie ! Vous bénéficiez de 15 jours d\'essai gratuit.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setIsLoading(false);
    }
  };

  const updateForm = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  if (isComplete) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-heading font-semibold mb-2">Bienvenue chez ResaWork !</h2>
            <p className="text-muted-foreground mb-4">
              Votre compte entreprise a été créé avec succès.
            </p>
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl mb-6">
              <div className="flex items-center justify-center gap-2 text-green-700 mb-2">
                <Gift className="w-5 h-5" />
                <span className="font-semibold">Essai gratuit activé</span>
              </div>
              <p className="text-sm text-green-600">
                Vous bénéficiez de <strong>15 jours d'essai gratuit</strong> pour découvrir toutes les fonctionnalités de ResaWork.
              </p>
            </div>
            <Button 
              onClick={() => navigate('/login')} 
              className="rounded-full w-full"
              data-testid="goto-login-btn"
            >
              Se connecter
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Compact */}
      <header className="border-b border-border">
        <div className="container mx-auto px-4 h-12 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="h-7" />
          </Link>
          <Button variant="ghost" size="sm" onClick={() => navigate('/login')} className="text-sm">
            Déjà inscrit ? Connexion
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Progress Steps - Compact */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                    index < currentStep 
                      ? 'bg-primary text-primary-foreground' 
                      : index === currentStep
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {index < currentStep ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <step.icon className="w-4 h-4" />
                  )}
                </div>
                {index < STEPS.length - 1 && (
                  <div 
                    className={`hidden sm:block w-12 lg:w-20 h-0.5 mx-1 rounded ${
                      index < currentStep ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="text-center">
            <h2 className="text-lg font-heading font-semibold">{STEPS[currentStep].title}</h2>
            <p className="text-xs text-muted-foreground">Étape {currentStep + 1} sur {STEPS.length}</p>
          </div>
        </div>

        {/* Form Card */}
        <Card className="shadow-floating">
          <CardContent className="p-5">
            {/* Step 1: Company Info */}
            {currentStep === 0 && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom de l'entreprise *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="name"
                      data-testid="onboarding-name"
                      placeholder="Mon Entreprise SARL"
                      value={formData.name}
                      onChange={(e) => updateForm('name', e.target.value)}
                      className={`pl-10 ${errors.name ? 'border-destructive' : ''}`}
                    />
                  </div>
                  {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="siret">SIRET (optionnel)</Label>
                  <Input
                    id="siret"
                    data-testid="onboarding-siret"
                    placeholder="123 456 789 00012"
                    value={formData.siret}
                    onChange={(e) => updateForm('siret', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Adresse *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="address"
                      data-testid="onboarding-address"
                      placeholder="123 Rue de Paris"
                      value={formData.address}
                      onChange={(e) => updateForm('address', e.target.value)}
                      className={`pl-10 ${errors.address ? 'border-destructive' : ''}`}
                    />
                  </div>
                  {errors.address && <p className="text-sm text-destructive">{errors.address}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="postal_code">Code postal *</Label>
                    <Input
                      id="postal_code"
                      data-testid="onboarding-postal"
                      placeholder="75001"
                      value={formData.postal_code}
                      onChange={(e) => updateForm('postal_code', e.target.value)}
                      className={errors.postal_code ? 'border-destructive' : ''}
                    />
                    {errors.postal_code && <p className="text-sm text-destructive">{errors.postal_code}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">Ville *</Label>
                    <Input
                      id="city"
                      data-testid="onboarding-city"
                      placeholder="Paris"
                      value={formData.city}
                      onChange={(e) => updateForm('city', e.target.value)}
                      className={errors.city ? 'border-destructive' : ''}
                    />
                    {errors.city && <p className="text-sm text-destructive">{errors.city}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Billing Info */}
            {currentStep === 1 && (
              <div className="space-y-4 animate-fade-in">
                <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
                  <input
                    type="checkbox"
                    id="use_same_address"
                    checked={formData.use_same_address}
                    onChange={(e) => updateForm('use_same_address', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <Label htmlFor="use_same_address" className="text-sm cursor-pointer">
                    Utiliser la même adresse que l'entreprise
                  </Label>
                </div>

                {!formData.use_same_address && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="billing_address">Adresse de facturation *</Label>
                      <div className="relative">
                        <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="billing_address"
                          placeholder="123 Rue de la Facturation"
                          value={formData.billing_address}
                          onChange={(e) => updateForm('billing_address', e.target.value)}
                          className={`pl-10 ${errors.billing_address ? 'border-destructive' : ''}`}
                        />
                      </div>
                      {errors.billing_address && <p className="text-sm text-destructive">{errors.billing_address}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="billing_postal_code">Code postal *</Label>
                        <Input
                          id="billing_postal_code"
                          placeholder="75001"
                          value={formData.billing_postal_code}
                          onChange={(e) => updateForm('billing_postal_code', e.target.value)}
                          className={errors.billing_postal_code ? 'border-destructive' : ''}
                        />
                        {errors.billing_postal_code && <p className="text-sm text-destructive">{errors.billing_postal_code}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="billing_city">Ville *</Label>
                        <Input
                          id="billing_city"
                          placeholder="Paris"
                          value={formData.billing_city}
                          onChange={(e) => updateForm('billing_city', e.target.value)}
                          className={errors.billing_city ? 'border-destructive' : ''}
                        />
                        {errors.billing_city && <p className="text-sm text-destructive">{errors.billing_city}</p>}
                      </div>
                    </div>
                  </>
                )}

                {formData.use_same_address && (
                  <div className="p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Adresse de facturation</p>
                    <p>{formData.address}, {formData.postal_code} {formData.city}, {formData.country}</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Branding */}
            {currentStep === 2 && (
              <div className="space-y-4 animate-fade-in">
                <p className="text-sm text-muted-foreground mb-4">
                  Personnalisez l'apparence de vos widgets de réservation. Ces paramètres peuvent être modifiés plus tard.
                </p>

                {/* Logo Upload */}
                <div className="space-y-2">
                  <Label>Logo de l'entreprise (optionnel)</Label>
                  <div className="flex items-center gap-4">
                    {formData.logo_url ? (
                      <div className="relative">
                        <img 
                          src={formData.logo_url} 
                          alt="Logo" 
                          className="w-16 h-16 object-cover border rounded-full bg-white"
                        />
                        <button
                          type="button"
                          onClick={() => updateForm('logo_url', '')}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => fileInputRef.current?.click()}
                        className="w-16 h-16 border-2 border-dashed border-muted-foreground/30 rounded-lg flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
                      >
                        <Upload className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <div className="text-sm text-muted-foreground">
                      <p>Cliquez pour télécharger</p>
                      <p className="text-xs">PNG, JPG (max 2 Mo)</p>
                    </div>
                  </div>
                </div>

                {/* Color Selection - Custom Color Picker */}
                <div className="space-y-2">
                  <Label>Couleur principale</Label>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <input
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => updateForm('primary_color', e.target.value)}
                        className="w-16 h-16 rounded-lg cursor-pointer border-2 border-muted hover:border-primary transition-colors"
                        style={{ padding: '2px' }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div 
                          className="w-8 h-8 rounded-md border"
                          style={{ backgroundColor: formData.primary_color }}
                        />
                        <Input
                          type="text"
                          value={formData.primary_color}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                              updateForm('primary_color', val);
                            }
                          }}
                          placeholder="#84CC16"
                          className="w-28 font-mono text-sm"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Cliquez sur le carré pour choisir une couleur ou entrez un code HEX
                      </p>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Aperçu</p>
                  <div className="flex items-center gap-3">
                    {formData.logo_url ? (
                      <img src={formData.logo_url} alt="Logo" className="w-10 h-10 object-cover rounded-full border bg-white" />
                    ) : (
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: formData.primary_color }}
                      >
                        {formData.name ? formData.name[0].toUpperCase() : 'E'}
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{formData.name || 'Votre entreprise'}</p>
                      <Button 
                        size="sm" 
                        className="mt-1 h-7 text-xs"
                        style={{ backgroundColor: formData.primary_color }}
                      >
                        Réserver
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Contact Person */}
            {currentStep === 3 && (
              <div className="space-y-4 animate-fade-in">
                <p className="text-sm text-muted-foreground mb-4">
                  Désignez une personne de contact pour la relation avec ResaWork.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="contact_name">Nom et prénom *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="contact_name"
                      data-testid="onboarding-contact-name"
                      placeholder="Jean Dupont"
                      value={formData.contact_name}
                      onChange={(e) => updateForm('contact_name', e.target.value)}
                      className={`pl-10 ${errors.contact_name ? 'border-destructive' : ''}`}
                    />
                  </div>
                  {errors.contact_name && <p className="text-sm text-destructive">{errors.contact_name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_role">Fonction (optionnel)</Label>
                  <Input
                    id="contact_role"
                    placeholder="Gérant, Responsable, etc."
                    value={formData.contact_role}
                    onChange={(e) => updateForm('contact_role', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="contact_email"
                      type="email"
                      data-testid="onboarding-contact-email"
                      placeholder="jean.dupont@entreprise.fr"
                      value={formData.contact_email}
                      onChange={(e) => updateForm('contact_email', e.target.value)}
                      className={`pl-10 ${errors.contact_email ? 'border-destructive' : ''}`}
                    />
                  </div>
                  {errors.contact_email && <p className="text-sm text-destructive">{errors.contact_email}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Téléphone (optionnel)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="contact_phone"
                      type="tel"
                      placeholder="01 23 45 67 89"
                      value={formData.contact_phone}
                      onChange={(e) => updateForm('contact_phone', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Account */}
            {currentStep === 4 && (
              <div className="space-y-4 animate-fade-in">
                {/* Trial info banner */}
                <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-center gap-2 text-green-700 mb-1">
                    <Gift className="w-4 h-4" />
                    <span className="font-medium text-sm">Essai gratuit de 15 jours</span>
                  </div>
                  <p className="text-xs text-green-600">
                    Aucune carte bancaire requise. Accès complet à toutes les fonctionnalités.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email de connexion *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      data-testid="onboarding-email"
                      placeholder="admin@entreprise.fr"
                      value={formData.email}
                      onChange={(e) => updateForm('email', e.target.value)}
                      className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
                    />
                  </div>
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  <p className="text-xs text-muted-foreground">
                    Cet email sera utilisé pour vous connecter à votre espace entreprise.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="onboarding-password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => updateForm('password', e.target.value)}
                      className={`pl-10 pr-10 ${errors.password ? 'border-destructive' : ''}`}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground cursor-pointer rounded-md hover:bg-muted transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="onboarding-confirm-password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => updateForm('confirmPassword', e.target.value)}
                      className={`pl-10 ${errors.confirmPassword ? 'border-destructive' : ''}`}
                    />
                  </div>
                  {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
                </div>
                
                {/* Recap */}
                <div className="p-3 bg-muted rounded-xl text-sm">
                  <p className="font-medium text-foreground mb-2">Récapitulatif</p>
                  <ul className="space-y-1 text-muted-foreground text-xs">
                    <li><strong>Entreprise:</strong> {formData.name}</li>
                    <li><strong>Adresse:</strong> {formData.address}, {formData.postal_code} {formData.city}</li>
                    <li><strong>Contact:</strong> {formData.contact_name} ({formData.contact_email})</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
              <Button
                variant="ghost"
                onClick={prevStep}
                disabled={currentStep === 0}
                className="rounded-full"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              
              <Button
                onClick={nextStep}
                disabled={isLoading}
                className="rounded-full"
                size="sm"
                data-testid="onboarding-next-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : currentStep === STEPS.length - 1 ? (
                  <>
                    Créer mon compte
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  <>
                    Continuer
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-4">
          En créant un compte, vous acceptez nos{' '}
          <a href="#" className="text-primary hover:underline">conditions d'utilisation</a>
          {' '}et notre{' '}
          <a href="#" className="text-primary hover:underline">politique de confidentialité</a>.
        </p>
      </div>
    </div>
  );
}
